import React, { useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Col,
    Descriptions,
    Modal,
    Progress,
    Row,
    Select,
    Space,
    Statistic,
    Typography,
} from 'antd';
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    ExclamationCircleOutlined,
    PlayCircleOutlined,
    ReloadOutlined,
    SyncOutlined,
    WifiOutlined,
} from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';
import { showSuccessNotification, showErrorNotification } from '@/components/common/utils/notify';
import {
    SyncTriggerType,
    useLoaderHealth,
    useResetMetrics,
    useSyncStatus,
    useTestConnectivity,
    useTriggerSync,
} from '../hooks/useSyncMonitoring';

const { Title, Text } = Typography;
const { Option } = Select;

// ── helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso?: string | null): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleString();
}

function syncPercent(synced: number, total: number): number {
    if (!total) return 0;
    return Math.round((synced / total) * 100);
}

// ── Sync type card ────────────────────────────────────────────────────────────

interface SyncCardProps {
    label: string;
    unsynced: number;
    synced: number;
    totalProcessed: number;
    dark: boolean;
    onTrigger: () => void;
    triggering: boolean;
}

const SyncTypeCard: React.FC<SyncCardProps> = ({
    label, unsynced, synced, totalProcessed, dark, onTrigger, triggering,
}) => {
    const total = synced + unsynced;
    const pct = syncPercent(synced, total);

    return (
        <Card
            size="small"
            className={dark ? 'bg-gray-800' : ''}
            title={<Text strong>{label}</Text>}
            extra={
                <Button
                    size="small"
                    icon={<PlayCircleOutlined />}
                    loading={triggering}
                    onClick={onTrigger}
                >
                    Sync now
                </Button>
            }
        >
            <Progress
                percent={pct}
                size="small"
                status={unsynced > 0 ? 'active' : 'success'}
                className="mb-2"
            />
            <Row gutter={8}>
                <Col span={8}>
                    <Statistic
                        title="Pending"
                        value={unsynced}
                        valueStyle={{ fontSize: 18, color: unsynced > 0 ? '#faad14' : '#52c41a' }}
                    />
                </Col>
                <Col span={8}>
                    <Statistic title="Synced" value={synced} valueStyle={{ fontSize: 18, color: '#52c41a' }} />
                </Col>
                <Col span={8}>
                    <Statistic title="Processed" value={totalProcessed} valueStyle={{ fontSize: 18 }} />
                </Col>
            </Row>
        </Card>
    );
};

// ── Main dashboard ────────────────────────────────────────────────────────────

const SyncMonitoringDashboard: React.FC = () => {
    const { dark } = useTheme();
    const { data: status, isLoading, refetch } = useSyncStatus();
    const { data: health } = useLoaderHealth();
    const { mutateAsync: trigger, isPending: triggering } = useTriggerSync();
    const { mutateAsync: testConn, isPending: testingConn } = useTestConnectivity();
    const { mutateAsync: resetMetrics } = useResetMetrics();

    const [triggerModalOpen, setTriggerModalOpen] = useState(false);
    const [selectedType, setSelectedType] = useState<SyncTriggerType>('all');
    const [connResult, setConnResult] = useState<any>(null);
    const [connModalOpen, setConnModalOpen] = useState(false);

    const handleTrigger = async () => {
        try {
            const result = await trigger(selectedType);
            setTriggerModalOpen(false);
            if (result.status === 'OK') {
                showSuccessNotification('Sync Complete', result.message, dark);
            } else {
                showErrorNotification('Sync Failed', result.message, dark);
            }
        } catch {
            showErrorNotification('Trigger Failed', 'Could not start sync. Check gateway connectivity.', dark);
        }
    };

    const handleTestConnectivity = async () => {
        try {
            const result = await testConn(undefined as any);
            setConnResult(result);
            setConnModalOpen(true);
        } catch (err: any) {
            setConnResult({ reachable: false, error: err?.message ?? 'Request failed' });
            setConnModalOpen(true);
        }
    };

    const handleResetMetrics = async () => {
        await resetMetrics(undefined as any);
        showSuccessNotification('Metrics Reset', 'Loader counters have been cleared.', dark);
    };

    const isUnhealthy = status && (!status.healthy || status.failedBatches > 0);
    const hasError = status?.lastError;

    return (
        <div>
            {/* Header */}
            <div className="flex justify-between items-center mb-4">
                <Title level={4} style={{ margin: 0 }} className={dark ? 'text-white' : ''}>
                    Payment Sync Monitoring
                </Title>
                <Space>
                    <Button icon={<WifiOutlined />} loading={testingConn} onClick={handleTestConnectivity}>
                        Test Connectivity
                    </Button>
                    <Button icon={<ReloadOutlined />} loading={isLoading} onClick={() => refetch()}>
                        Refresh
                    </Button>
                    <Button
                        type="primary"
                        icon={<PlayCircleOutlined />}
                        onClick={() => setTriggerModalOpen(true)}
                    >
                        Trigger Sync
                    </Button>
                </Space>
            </div>

            {/* Health alerts */}
            {isUnhealthy && (
                <Alert
                    type="warning"
                    showIcon
                    icon={<ExclamationCircleOutlined />}
                    message={`Sync service has ${status!.failedBatches} failed batch(es)`}
                    description={
                        <Space direction="vertical" size={2}>
                            {status?.lastError && <Text type="danger">Last error: {status.lastError}</Text>}
                            <Text type="secondary">Last failure: {fmtDate(status?.lastFailedRun)}</Text>
                        </Space>
                    }
                    action={
                        <Button size="small" onClick={handleResetMetrics}>
                            Reset Counters
                        </Button>
                    }
                    className="mb-4"
                />
            )}

            {/* Service status bar */}
            <Card size="small" className={`mb-4 ${dark ? 'bg-gray-800' : ''}`}>
                <Row gutter={24} align="middle">
                    <Col>
                        {health?.status === 'HEALTHY'
                            ? <Text type="success"><CheckCircleOutlined /> Gateway Sync: Healthy</Text>
                            : <Text type="danger"><CloseCircleOutlined /> Gateway Sync: Degraded</Text>
                        }
                    </Col>
                    <Col>
                        <Text type="secondary">Institution: {status?.institutionId ?? '—'}</Text>
                    </Col>
                    <Col>
                        <Text type="secondary">
                            Last success: {fmtDate(status?.lastSuccessfulRun)}
                        </Text>
                    </Col>
                    <Col>
                        <Text type="secondary">
                            Failed batches: {status?.failedBatches ?? 0}
                        </Text>
                    </Col>
                </Row>
            </Card>

            {/* Per-type sync cards */}
            <Row gutter={16} className="mb-4">
                <Col xs={24} lg={8}>
                    <SyncTypeCard
                        label="Bills"
                        unsynced={status?.bills.unsynced ?? 0}
                        synced={status?.bills.synced ?? 0}
                        totalProcessed={status?.bills.totalProcessed ?? 0}
                        dark={dark}
                        triggering={triggering}
                        onTrigger={async () => {
                            try {
                                const r = await trigger('bills');
                                r.status === 'OK'
                                    ? showSuccessNotification('Done', r.message, dark)
                                    : showErrorNotification('Failed', r.message, dark);
                            } catch {
                                showErrorNotification('Error', 'Bill sync failed', dark);
                            }
                        }}
                    />
                </Col>
                <Col xs={24} lg={8}>
                    <SyncTypeCard
                        label="Products"
                        unsynced={status?.products.unsynced ?? 0}
                        synced={status?.products.synced ?? 0}
                        totalProcessed={status?.products.totalProcessed ?? 0}
                        dark={dark}
                        triggering={triggering}
                        onTrigger={async () => {
                            try {
                                const r = await trigger('products');
                                r.status === 'OK'
                                    ? showSuccessNotification('Done', r.message, dark)
                                    : showErrorNotification('Failed', r.message, dark);
                            } catch {
                                showErrorNotification('Error', 'Product sync failed', dark);
                            }
                        }}
                    />
                </Col>
                <Col xs={24} lg={8}>
                    <SyncTypeCard
                        label="Profiles"
                        unsynced={status?.profiles.unsynced ?? 0}
                        synced={status?.profiles.synced ?? 0}
                        totalProcessed={status?.profiles.totalProcessed ?? 0}
                        dark={dark}
                        triggering={triggering}
                        onTrigger={async () => {
                            try {
                                const r = await trigger('profiles');
                                r.status === 'OK'
                                    ? showSuccessNotification('Done', r.message, dark)
                                    : showErrorNotification('Failed', r.message, dark);
                            } catch {
                                showErrorNotification('Error', 'Profile sync failed', dark);
                            }
                        }}
                    />
                </Col>
            </Row>

            {/* Trigger all modal */}
            <Modal
                title="Trigger Manual Sync"
                open={triggerModalOpen}
                onOk={handleTrigger}
                onCancel={() => setTriggerModalOpen(false)}
                confirmLoading={triggering}
                okText="Start"
            >
                <div className="mb-3">
                    <Text>Select which data to synchronise with the payment gateway:</Text>
                </div>
                <Select
                    value={selectedType}
                    onChange={setSelectedType}
                    style={{ width: '100%' }}
                >
                    <Option value="all">All (Bills + Products + Profiles)</Option>
                    <Option value="bills">Bills only</Option>
                    <Option value="products">Products only</Option>
                    <Option value="profiles">Profiles only</Option>
                </Select>
                <div className="mt-3">
                    <Text type="secondary">
                        Manual sync runs immediately in the background. The counters on this
                        page will update within 15 seconds.
                    </Text>
                </div>
            </Modal>

            {/* Connectivity modal */}
            <Modal
                title="Connectivity Test"
                open={connModalOpen}
                onCancel={() => setConnModalOpen(false)}
                footer={<Button onClick={() => setConnModalOpen(false)}>Close</Button>}
            >
                {connResult && (
                    <>
                        <Alert
                            type={connResult.reachable ? 'success' : 'error'}
                            showIcon
                            message={connResult.reachable ? 'Gateway is reachable' : 'Gateway is NOT reachable'}
                            className="mb-3"
                        />
                        <Descriptions column={1} size="small" bordered>
                            <Descriptions.Item label="Gateway URL">{connResult.gatewayUrl}</Descriptions.Item>
                            <Descriptions.Item label="Institution ID">{connResult.institutionId}</Descriptions.Item>
                            <Descriptions.Item label="Response Time">
                                {connResult.responseTimeMs != null ? `${connResult.responseTimeMs} ms` : '—'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Checked At">{fmtDate(connResult.checkedAt)}</Descriptions.Item>
                            {connResult.error && (
                                <Descriptions.Item label="Error">
                                    <Text type="danger">{connResult.error}</Text>
                                </Descriptions.Item>
                            )}
                        </Descriptions>
                    </>
                )}
            </Modal>
        </div>
    );
};

export default SyncMonitoringDashboard;
